import { confirmDialog } from '../components/ui/ConfirmDialog';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Contact, Property, Requirement, PipelineColumn } from '../types/types';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { applyLeadPriorities } from '../lib/lead-priority';
import { type ContactListServerFilters, narrowContacts, tagEmbed, intakeColumnIdOf } from '../lib/contact-list-filters';
import { useRequestId } from '../hooks/useRequestId';
import { readPropertiesFiltersFromSession } from '../lib/properties-filter-storage';
import { isInPublishRenewalWindow, PUBLISH_RENEWAL_WINDOW_DAYS } from '../lib/publish-renewal';
import { shareRenewalCutoffISO, isInShareRenewalWindow } from '../lib/share-renewal';
import { slugifyAmenity } from '../lib/amenity-slug';
import { checkShareCompliance, submitShareAppeal } from '../lib/share-compliance';
import { canViewAllOwners } from '../utils/permissions';
import { applyPendingOverlay } from '../lib/undoable';
import { RegistrarVentaModal } from '../components/ui/RegistrarVentaModal';

export interface Proyecto {
  id: string;
  agency_id?: string;
  nombre: string;
  desarrollador?: string;
  descripcion?: string;
  ubicacion?: string;
  estado?: 'En Construcción' | 'Planos' | 'Terminado';
  entrega?: string;
  unidadesTotales?: number;      // alias for unidades_totales (mapped on fetch)
  unidadesDisponibles?: number;  // alias for unidades_disponibles (mapped on fetch)
  unidades_totales?: number;
  unidades_disponibles?: number;
  // Live count of properties linked via properties.proyecto_id (mapped on fetch).
  // Distinct from unidadesTotales, which is a manually-entered figure.
  asignadas?: number;
  imagen?: string;
  contacto?: { nombre: string; telefono?: string; email?: string };
  galeria?: string[];
  documentos?: { nombre: string; url: string }[];
  // Location
  department_id?: number;
  city_id?: number;
  sector_id?: number;
  address?: string;
  latitude?: string;
  longitude?: string;
  show_exact_location?: boolean;
  country_id?: number;
  legacy_id?: number;
  // When true, hidden from the agency's public client website (portal). CRM-only flag.
  hide_from_website?: boolean;
  // Public website card options (only meaningful when the agency has a site).
  show_units_on_website?: boolean;      // show UNIDADES/DISPONIBLES on the card (opt-in)
  show_assigned_properties_on_website?: boolean; // show a "Propiedades del Proyecto" section on the public project detail page (opt-in)
  precio_desde?: string | null;         // optional free-text "Precios desde" value
  created_at?: string;
  [key: string]: any;
}

const ITEMS_PER_PAGE = 14;
// Requirements list uses a 4-col grid and smaller cards than properties — fetch
// in larger batches so most agencies fit in the first page (median ~10/agency).
// Top-end agencies (CityMax 10560, Olga 8688) page through. Without this cap,
// the unbounded query against v_requirements_with_match_count + the 10k-card
// render froze the browser on big agencies (Gap 39).
const REQUIREMENTS_PAGE_SIZE = 50;
// Contacts feed the prospectos kanban which needs leads grouped by column —
// pagination has to keep enough headroom for all kanban columns to populate
// for typical agencies (median <500/agency). Heavy agencies (Olga 12188,
// CityMax 10655) page through with "Cargar más" loading the next 500 most-
// recent contacts each time. Day-to-day work usually fits in the first page.
const CONTACTS_PAGE_SIZE = 500;

// Map V2 DB enum (`property_status`) to the Spanish UI label (`status`).
// PropertyCard / PropertyDetailView / StatusPicker still drive off the legacy
// label, so we synthesize it here. Migrated rows arrive with property_status
// set to the enum and no `status`, so without this they all fell through to
// the "Disponible" default in the card.
export const PROPERTY_STATUS_LABEL: Record<string, string> = {
  available: 'Disponible',
  reserved: 'Con Reserva',
  sold: 'Vendido',
  rented: 'Alquilado',
  off_market: 'Fuera de mercado',
};
const PROPERTY_STATUS_ENUM: Record<string, string> = {
  'Disponible': 'available',
  'Con Reserva': 'reserved',
  'Vendido': 'sold',
  'Alquilado': 'rented',
  'Fuera de mercado': 'off_market',
};

// Server-side filters pushed into the properties query so pagination respects
// them. Without this, the user can have e.g. 2 published properties at row
// #15+ while the first page (14 rows) holds none, and the client-only filter
// returns 0 matches even though the DB has them.
// Sort field for the Mis Propiedades list toolbar. 'default' = the per-tab
// sort; every other value pins a specific column. Direction (asc/desc) is a
// separate concern handled by sortDir, so each field appears once.
export type PropertySortBy =
  | 'default'
  | 'created_date'
  | 'price'
  | 'updated_date'
  | 'built_up_area'
  | 'published_date';

export type PropertyListServerFilters = {
  activeTab?: 'activas' | 'expiradas' | 'borradores' | 'archivadas' | 'eliminadas';
  // Sort selector from the list toolbar. 'default' keeps the per-tab sort
  // (Activas → last_activity_at, others → created_at). Any explicit field
  // (created_date / price / updated_date / built_up_area / published_date)
  // forces that column on every tab regardless of recent activity. sortDir
  // flips the direction (defaults to 'desc' = newest/most-recent first).
  sortBy?: PropertySortBy;
  sortDir?: 'asc' | 'desc';
  publishStatus?: 'published' | 'unpublished' | 'expired';
  // 'expiring' is not a share_status value — it means "already expired OR
  // lapsing within SHARE_REVIEW_WINDOW_DAYS" (see lib/share-expiry.ts).
  shareStatus?: 'shared' | 'not_shared' | 'expiring';
  operation?: 'sale' | 'rent' | 'sale_rent' | 'vacational';
  propertyStatus?: 'available' | 'reserved' | 'sold' | 'rented' | 'off_market';
  department_id?: number;
  city_ids?: number[];
  sector_ids?: number[];
  agent_ids?: string[];
  // Projects (proyectos) the agency created; filters on properties.proyecto_id.
  proyecto_ids?: string[];
  property_type_ids?: number[];
  is_featured?: boolean;
  // Numeric range / >= filters from the advanced filter modal. Same motivation
  // as the rest: client-only filtering on a 14-row page silently returns 0
  // results past page 1, so push these down to the server.
  min_price?: number;
  max_price?: number;
  // Bedrooms is a chip-array. Selected exact-value matches go into the IN
  // list; the special "5+" chip (value 5) trips the gte flag so the SQL
  // becomes `bedrooms IN (...) OR bedrooms >= 5`.
  bedrooms?: number[];
  min_baths?: number;
  min_parking?: number;
  min_built_up_area?: number;
  max_built_up_area?: number;
  min_lot_size_m2?: number;
  max_lot_size_m2?: number;
  // Selected amenity names. Matched against properties.services (JSONB array
  // of names) — newer CRM saves write amenities there; the property_amenities
  // junction is mostly used by legacy data. ALL selected amenities must be
  // present on the property to match.
  amenities?: string[];
  // Free-text search (debounced from the search input). When set, the server
  // narrows by case-insensitive partial match against ii_id OR title — needed
  // for super_admin (cross-agency lists) and agencies with hundreds of
  // properties where the wanted record sits past page 1 and the client-only
  // filter never sees it. Mirrors the client-side searchQuery behavior in
  // PropiedadesView so search results stay consistent across pages.
  search?: string;
};

// Server-side filters for the requirements list. Same motivation as
// PropertyListServerFilters: RequirementsView filtered the in-memory slice
// (first REQUIREMENTS_PAGE_SIZE by created_at DESC), so on agencies with >50
// requirements (193 of 417 on prod) a filter like operation=rent silently
// dropped every match past the loaded page. Pushed to the server so the whole
// dataset is filtered.
export type RequirementListServerFilters = {
  operation?: string;
  status?: string;
  agent_id?: string;
  property_type_ids?: number[];
  created_from?: string;
  created_to?: string;
};

interface CRMContextType {
  // Properties
  properties: Property[];
  setProperties: React.Dispatch<React.SetStateAction<Property[]>>;
  sharedProperties: Property[];
  setSharedProperties: React.Dispatch<React.SetStateAction<Property[]>>;
  fetchProperties: (page: number, append?: boolean, serverFilters?: PropertyListServerFilters) => Promise<void>;
  handleStatusChange: (id: string, newStatus: string) => void;
  handlePublishToggle: (id: string) => void;
  handleShareToggle: (id: string) => void;
  /** Open the shared "Registrar venta" modal for a property (optionally with a known buyer). */
  openRecordSale: (property: Property, contact?: Contact | null) => void;
  hasMoreProperties: boolean;
  propertiesTotalCount: number | null;
  isLoadingMoreProperties: boolean;
  isPropertiesInitialized: boolean;
  handleLoadMoreProperties: () => void;

  // Contacts
  leads: Contact[];
  setLeads: React.Dispatch<React.SetStateAction<Contact[]>>;
  owners: Contact[];
  setOwners: React.Dispatch<React.SetStateAction<Contact[]>>;
  hasMoreContacts: boolean;
  isLoadingMoreContacts: boolean;
  handleLoadMoreContacts: () => void;
  fetchContacts: (page: number, append?: boolean, leadColsAtFetch?: PipelineColumn[], serverFilters?: ContactListServerFilters) => Promise<void>;
  // Server-side exact totals (is_lead / is_owner). True counts independent of the
  // paginated leads/owners slices; refreshed via refreshContactCounts.
  leadsTotalCount: number | null;
  ownersTotalCount: number | null;
  refreshContactCounts: () => Promise<void>;
  // Exact count of contacts matching `filters`; with hiddenColumnIds, only what
  // the board renders (not archived, outside those columns).
  countContacts: (filters: ContactListServerFilters, role: 'lead' | 'owner', hiddenColumnIds?: string[]) => Promise<number | null>;

  // Pipeline columns
  leadsColumns: PipelineColumn[];
  setLeadsColumns: React.Dispatch<React.SetStateAction<PipelineColumn[]>>;
  ownersColumns: PipelineColumn[];
  setOwnersColumns: React.Dispatch<React.SetStateAction<PipelineColumn[]>>;
  /** Re-fetch lead + owner pipeline columns from the DB (after a settings edit). */
  refreshPipelineColumns: () => Promise<void>;

  // Requirements
  requirements: Requirement[];
  setRequirements: React.Dispatch<React.SetStateAction<Requirement[]>>;
  sharedRequirements: Requirement[];
  setSharedRequirements: React.Dispatch<React.SetStateAction<Requirement[]>>;
  hasMoreRequirements: boolean;
  isLoadingMoreRequirements: boolean;
  isRequirementsInitialized: boolean;
  handleLoadMoreRequirements: () => void;
  fetchRequirements: (page: number, append?: boolean, serverFilters?: RequirementListServerFilters) => Promise<void>;

  // Intercambio (cross-agency) lazy loaders. The shared-* lists are
  // expensive (cross-agency, no LIMIT), so we don't fetch them on
  // CRMContext mount anymore — only when a screen actually needs them.
  // Consumers call ensureShared* in a mount effect; the first call
  // fires the RPC, subsequent calls are no-ops. fetchShared* still
  // forces a refetch (e.g. pull-to-refresh on the Intercambio pages).
  isSharedPropertiesLoaded: boolean;
  isSharedRequirementsLoaded: boolean;
  ensureSharedProperties: () => Promise<void>;
  ensureSharedRequirements: () => Promise<void>;
  fetchSharedProperties: () => Promise<void>;
  fetchSharedRequirements: () => Promise<void>;

  // Projects
  proyectos: Proyecto[];
  setProyectos: React.Dispatch<React.SetStateAction<Proyecto[]>>;

  // Loading state
  isDataLoaded: boolean;
}

const CRMContext = createContext<CRMContextType | undefined>(undefined);

export const CRMProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile: authProfile, isImpersonating, isReadOnly } = useAuth();
  // An Academia student has no agency and never sees the CRM, so the whole
  // context behaves as signed out for them: no fetches, no realtime channels.
  const profile = authProfile?.role === 'student' ? null : authProfile;

  const blockReadOnly = useCallback((): boolean => {
    if (!isReadOnly) return false;
    try {
      // eslint-disable-next-line no-alert
      alert('Modo solo lectura: estás viendo esta cuenta como Super Admin. Sal del modo de suplantación para realizar cambios.');
    } catch {
      // ignore
    }
    return true;
  }, [isReadOnly]);

  // Properties
  const [properties, setProperties] = useState<Property[]>([]);
  const [sharedProperties, setSharedProperties] = useState<Property[]>([]);
  const [propertiesPage, setPropertiesPage] = useState(1);
  const [hasMoreProperties, setHasMoreProperties] = useState(true);
  // Exact total of rows matching the current server-side filter set (ignores
  // pagination range). null until the first fetch resolves. The list is
  // paginated, so this is the only source of a true "N results" count — the
  // loaded `properties` array only holds the pages fetched so far.
  const [propertiesTotalCount, setPropertiesTotalCount] = useState<number | null>(null);
  const [isLoadingMoreProperties, setIsLoadingMoreProperties] = useState(false);
  // Flips to true after the first non-superseded fetchProperties resolves.
  // Consumers gate their empty state on this so "No se encontraron" doesn't
  // flash on every visit while the first network round-trip is in flight.
  const [isPropertiesInitialized, setIsPropertiesInitialized] = useState(false);
  // Last server-side filters used for the properties list. Held in a ref so
  // handleLoadMoreProperties can append the next page with the same filters
  // without forcing fetchProperties to depend on a state variable (which
  // would re-create the callback and ripple through every consumer).
  const propertyFiltersRef = useRef<PropertyListServerFilters>({});
  // Same role as propertyFiltersRef but for the contacts query — held so
  // handleLoadMoreContacts can paginate with the active search filter.
  const contactFiltersRef = useRef<ContactListServerFilters>({});
  // Same role for the requirements query — held so handleLoadMoreRequirements
  // paginates with the active list filters.
  const requirementFiltersRef = useRef<RequirementListServerFilters>({});
  // Request-id guards. Each list-fetch captures an id from useRequestId()
  // and only writes to state if that id is still the latest when the await
  // resolves. Older parallel calls (StrictMode dual-mount, rapid tab
  // clicks, cleanup-as-refetch, token refresh mid-fetch) drop their
  // results instead of stomping a newer fetch with stale/empty rows.
  // Without this the last call to resolve wins regardless of order, which
  // produced the intermittent "no properties" empty state.
  const propertiesReqId = useRequestId();
  const requirementsReqId = useRequestId();
  const contactsReqId = useRequestId();
  const sharedPropertiesReqId = useRequestId();
  const sharedRequirementsReqId = useRequestId();

  // Contacts
  const [leads, setLeads] = useState<Contact[]>([]);
  const [owners, setOwners] = useState<Contact[]>([]);
  const [contactsPage, setContactsPage] = useState(1);
  const [hasMoreContacts, setHasMoreContacts] = useState(true);
  // Defaults to true so the kanban can show a skeleton from the very first
  // render — before fetchContacts() has had a chance to flip it on.
  const [isLoadingMoreContacts, setIsLoadingMoreContacts] = useState(true);
  // Exact totals for the prospectos / propietarios boards (independent of the
  // 500-row paginated slice). Refreshed on initial load + after add/delete.
  const [leadsTotalCount, setLeadsTotalCount] = useState<number | null>(null);
  const [ownersTotalCount, setOwnersTotalCount] = useState<number | null>(null);

  // Pipeline columns — start empty, fetched from DB
  const [leadsColumns, setLeadsColumns] = useState<PipelineColumn[]>([]);
  const [ownersColumns, setOwnersColumns] = useState<PipelineColumn[]>([]);
  // Mirror leadsColumns into a ref so fetchContacts can read the latest value
  // without listing leadsColumns as a useCallback dep (the array reference
  // changes whenever pipeline_columns is refetched, which would otherwise
  // refire the parent useEffect and trigger a full reload loop).
  const leadsColumnsRef = useRef<PipelineColumn[]>([]);
  useEffect(() => { leadsColumnsRef.current = leadsColumns; }, [leadsColumns]);

  // Requirements & Projects
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [sharedRequirements, setSharedRequirements] = useState<Requirement[]>([]);
  const [proyectos, setProyectos] = useState<Proyecto[]>([]);
  const [requirementsPage, setRequirementsPage] = useState(1);
  const [hasMoreRequirements, setHasMoreRequirements] = useState(true);
  const [isLoadingMoreRequirements, setIsLoadingMoreRequirements] = useState(false);
  // Same role as isPropertiesInitialized — gates the empty state so
  // "No se encontraron requerimientos" doesn't flash before the first
  // fetch resolves on a slow network round-trip.
  const [isRequirementsInitialized, setIsRequirementsInitialized] = useState(false);

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const fetchProperties = useCallback(async (
    page: number,
    append: boolean = false,
    serverFilters?: PropertyListServerFilters,
  ) => {
    if (!supabase || !profile) return;
    // When the caller passes filters, store them so a follow-up "Cargar más"
    // appends the next page with the same constraints. When omitted, reuse
    // whatever was last set (initial load uses the empty default).
    if (serverFilters !== undefined) {
      propertyFiltersRef.current = serverFilters;
    }
    const filters = propertyFiltersRef.current;
    const myReqId = propertiesReqId.next();
    try {
      setIsLoadingMoreProperties(true);
      const from = (page - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      // Owner contact is intentionally NOT joined here — it's PII gated by
      // can_view_all_owners and the property's agent_id, both checked on the
      // detail screen. Pulling owner into every list row would ship that PII
      // to every agent in the agency regardless of permission.
      // 2026-05-06: cover_photo_url is denormalized onto `properties` and kept
      // in sync by trg_sync_property_cover (migration 20260506060000). The
      // earlier batched IN(...) lookup on property_photos is gone — `*` now
      // pulls cover_photo_url directly. Full gallery is still loaded on the
      // property detail page (on-demand path).
      let propsQuery = supabase
        .from('properties')
        .select(`
          *,
          property_types (name),
          departments (name),
          cities (name),
          sectors (name),
          agent:profiles!properties_agent_id_fkey (id, full_name, photo_url, phone)
        `, { count: 'exact' })
        .range(from, to);

      // Sort: Activas bubbles freshly-acted-on rows (created / edited /
      // restored / renewed) via last_activity_at, which is a maintained
      // GREATEST of created_at/updated_at/restored_at/published_at/shared_at
      // (migration 20260510120000). created_at is the stable secondary key
      // for pagination. Other tabs are state-history views and keep
      // created_at DESC so the order matches "when this row entered the tab".
      // An explicit sort field (created_date / price / updated_date /
      // built_up_area / published_date) overrides this and pins that column on
      // every tab. sortDir flips ascending/descending on whichever sort is
      // active (defaults to descending = newest/most-recent first).
      const sortAsc = filters.sortDir === 'asc';
      // Explicit fields map to a single column. Nullable columns (price,
      // built_up_area, published_at) push NULLs to the bottom in BOTH
      // directions so unpriced / undimensioned / unpublished rows never
      // dominate the top of a descending sort; created_at is the stable
      // secondary key so ties don't reshuffle across pages.
      const SORT_COLUMN: Record<string, string> = {
        created_date: 'created_at',
        price: 'price',
        updated_date: 'updated_at',
        built_up_area: 'built_up_area',
        published_date: 'published_at',
      };
      const sortColumn = filters.sortBy ? SORT_COLUMN[filters.sortBy] : undefined;
      if (sortColumn) {
        propsQuery = propsQuery.order(sortColumn, { ascending: sortAsc, nullsFirst: false });
        if (sortColumn !== 'created_at') {
          propsQuery = propsQuery.order('created_at', { ascending: sortAsc });
        }
      } else if (filters.activeTab === 'activas') {
        propsQuery = propsQuery
          .order('last_activity_at', { ascending: sortAsc })
          .order('created_at', { ascending: sortAsc });
      } else {
        propsQuery = propsQuery.order('created_at', { ascending: sortAsc });
      }

      // Tab filter — translates the UI tab into the matching DB columns. The
      // client-side filter in PropiedadesView still runs as belt-and-
      // suspenders for legacy edge cases (status='Archivada', etc.) but the
      // server narrow is what makes pagination + filters consistent.
      // "Expiradas" = needs renewal: already expired OR still published but
      // inside the 7-day renewal window. Mirrors shouldShowRenovarPublish on
      // the client. Built once here so the tab and the publishStatus filter
      // below share the exact same definition.
      const renewalCutoff = new Date(Date.now() + PUBLISH_RENEWAL_WINDOW_DAYS * 86400000).toISOString();
      const expiringOrExpiredOr = `publish_status.eq.expired,and(publish_status.eq.published,publish_expires_at.lte.${renewalCutoff})`;

      if (filters.activeTab === 'activas') {
        // crm_status='active' (NOT just .neq('archived')) so the count:'exact'
        // total matches both the rendered grid (PropiedadesView drops drafts
        // client-side) and the "Todas" chip badge (get_property_filter_counts
        // scopes to crm_status='active'). .neq('archived') let drafts through,
        // which the grid then hid — inflating the header total by the draft
        // count (off-by-one when the agency has 1 draft). Drafts live in the
        // Borradores tab. crm_status is always one of active/archived/draft
        // (never NULL in prod), so this drops exactly the drafts.
        propsQuery = propsQuery
          .eq('is_deleted', false)
          .eq('crm_status', 'active');
        // Hide expired from Activas — unless the user is explicitly filtering
        // for the Expiradas set (chip / advanced modal), which intentionally
        // surfaces expired rows alongside soon-to-expire published ones.
        if (filters.publishStatus !== 'expired') {
          propsQuery = propsQuery.neq('publish_status', 'expired');
        }
      } else if (filters.activeTab === 'expiradas') {
        propsQuery = propsQuery.eq('is_deleted', false).or(expiringOrExpiredOr);
      } else if (filters.activeTab === 'borradores') {
        propsQuery = propsQuery.eq('is_deleted', false).eq('crm_status', 'draft');
      } else if (filters.activeTab === 'archivadas') {
        // Archivadas = soft-deleted but NOT permanently-deleted. For
        // non-super-admins the permanently_deleted_at clause is a no-op
        // (RLS hides those rows anyway); for super_admin it routes them to
        // the dedicated Eliminadas tab so pagination stays consistent.
        propsQuery = propsQuery
          .or('is_deleted.eq.true,crm_status.eq.archived')
          .is('permanently_deleted_at', null);
      } else if (filters.activeTab === 'eliminadas') {
        // Super-admin-only tab — surfaces rows permanently deleted from the
        // agency view. RLS already gates this to super_admin (see
        // properties.permanently_deleted_at clause on "Agents can read
        // properties in their agency"); the explicit narrow keeps the list
        // clean for paginated cross-agency lookups.
        propsQuery = propsQuery.not('permanently_deleted_at', 'is', null);
      }

      if (filters.publishStatus === 'expired') {
        propsQuery = propsQuery.or(expiringOrExpiredOr);
      } else if (filters.publishStatus) {
        propsQuery = propsQuery.eq('publish_status', filters.publishStatus);
      }
      if (filters.shareStatus === 'expiring') {
        // Shares that need a human look before they lapse. Must mirror
        // needsShareRenewal() in lib/share-renewal.ts exactly — the dashboard
        // pendiente counts with the helper, this query fills the grid.
        propsQuery = propsQuery
          .in('share_status', ['shared', 'expired'])
          .lte('share_expires_at', shareRenewalCutoffISO());
      } else if (filters.shareStatus) {
        propsQuery = propsQuery.eq('share_status', filters.shareStatus);
      }
      if (filters.is_featured) {
        propsQuery = propsQuery.eq('is_featured', true);
      }
      if (filters.operation) {
        // DB enum is 'sale'/'rent' — caller is responsible for passing those
        // values, not UI labels.
        propsQuery = propsQuery.eq('operation', filters.operation);
      }
      if (filters.propertyStatus) {
        propsQuery = propsQuery.eq('property_status', filters.propertyStatus);
      }
      if (filters.department_id) {
        propsQuery = propsQuery.eq('department_id', filters.department_id);
      }
      if (filters.city_ids && filters.city_ids.length > 0) {
        propsQuery = propsQuery.in('city_id', filters.city_ids);
      }
      if (filters.sector_ids && filters.sector_ids.length > 0) {
        propsQuery = propsQuery.in('sector_id', filters.sector_ids);
      }
      if (filters.agent_ids && filters.agent_ids.length > 0) {
        propsQuery = propsQuery.in('agent_id', filters.agent_ids);
      }
      if (filters.proyecto_ids && filters.proyecto_ids.length > 0) {
        propsQuery = propsQuery.in('proyecto_id', filters.proyecto_ids);
      }
      if (filters.property_type_ids && filters.property_type_ids.length > 0) {
        propsQuery = propsQuery.in('property_type_id', filters.property_type_ids);
      }
      if (typeof filters.min_price === 'number' && filters.min_price > 0) {
        propsQuery = propsQuery.gte('price', filters.min_price);
      }
      if (typeof filters.max_price === 'number' && filters.max_price > 0) {
        propsQuery = propsQuery.lte('price', filters.max_price);
      }
      if (filters.bedrooms && filters.bedrooms.length > 0) {
        // The "5+" chip is value 5 and means "bedrooms ≥ 5". Other chips are
        // exact matches. Build an OR of (in <exact list>) and (gte 5) — using
        // PostgREST's or() filter so the predicate stays a single AND clause
        // alongside the rest of the query.
        const hasFivePlus = filters.bedrooms.includes(5);
        const exact = filters.bedrooms.filter(b => b !== 5);
        if (hasFivePlus && exact.length > 0) {
          propsQuery = propsQuery.or(`bedrooms.in.(${exact.join(',')}),bedrooms.gte.5`);
        } else if (hasFivePlus) {
          propsQuery = propsQuery.gte('bedrooms', 5);
        } else {
          propsQuery = propsQuery.in('bedrooms', exact);
        }
      }
      if (typeof filters.min_baths === 'number' && filters.min_baths > 0) {
        propsQuery = propsQuery.gte('bathrooms', filters.min_baths);
      }
      if (typeof filters.min_parking === 'number' && filters.min_parking > 0) {
        propsQuery = propsQuery.gte('parking', filters.min_parking);
      }
      if (typeof filters.min_built_up_area === 'number' && filters.min_built_up_area > 0) {
        propsQuery = propsQuery.gte('built_up_area', filters.min_built_up_area);
      }
      if (typeof filters.max_built_up_area === 'number' && filters.max_built_up_area > 0) {
        propsQuery = propsQuery.lte('built_up_area', filters.max_built_up_area);
      }
      // Land size is a varas² (v²) field in this market — the filter input is
      // labelled V² and stores varas, so filter the lot_size_varas column.
      if (typeof filters.min_lot_size_m2 === 'number' && filters.min_lot_size_m2 > 0) {
        propsQuery = propsQuery.gte('lot_size_varas', filters.min_lot_size_m2);
      }
      if (typeof filters.max_lot_size_m2 === 'number' && filters.max_lot_size_m2 > 0) {
        propsQuery = propsQuery.lte('lot_size_varas', filters.max_lot_size_m2);
      }
      if (filters.amenities && filters.amenities.length > 0) {
        // services is a JSONB array of amenity tokens. Two forms coexist in
        // prod: legacy migrated rows store slug-style keys ("casa_club_gimnasio",
        // "balcon") while new CRM saves store canonical names ("Casa club:
        // Gimnasio", "Balcón"). Legacy dominates by ~30x, so a name-only
        // `contains` returned almost nothing. Match either form per amenity
        // via .or(); multiple .or() calls AND together, so the final filter
        // is (name1 OR slug1) AND (name2 OR slug2) … — every selected amenity
        // must be present in some form.
        for (const name of filters.amenities) {
          const slug = slugifyAmenity(name);
          const branches = [
            `services.cs.["${name.replace(/"/g, '\\"')}"]`,
            `services.cs.["${slug}"]`,
          ];
          propsQuery = propsQuery.or(branches.join(','));
        }
      }
      // Free-text search — case-insensitive partial across ii_id, title, and
      // location names. Cities and sectors are joined via FK so we resolve the
      // matching IDs first (parallel) and feed them into the parent query's
      // or() clause as `city_id.in.(...)` / `sector_id.in.(...)`. This avoids
      // requiring agencies!inner / cities!inner / sectors!inner on the parent
      // SELECT (which would drop properties that lack a city or sector).
      // PostgREST OR-filter special chars (",()*) are stripped before
      // interpolation; cities + sectors tables are bounded (~370 / ~1100
      // rows) so the lookups are O(small).
      if (filters.search) {
        const safe = filters.search.trim().replace(/[(),*]/g, '');
        if (safe) {
          // Owner-name search: resolve agency contacts whose name matches, then
          // narrow properties by owner_id below. Scope the lookup the same way
          // the properties query is scoped (planner uses the agency index, and
          // the candidate set stays bounded). RLS enforces agency isolation too.
          let ownersLookup = supabase
            .from('contacts')
            .select('id')
            .eq('is_deleted', false)
            .ilike('name', `%${safe}%`)
            .limit(500);
          if (profile.agency_id && !(profile.role === 'super_admin' && !isImpersonating)) {
            ownersLookup = ownersLookup.eq('agency_id', profile.agency_id);
          }
          const [citiesRes, sectorsRes, ownersRes] = await Promise.all([
            supabase.from('cities').select('id').ilike('name', `%${safe}%`).limit(200),
            supabase.from('sectors').select('id').ilike('name', `%${safe}%`).limit(200),
            ownersLookup,
          ]);
          const cityIds = (citiesRes.data || []).map((r: any) => r.id);
          const sectorIds = (sectorsRes.data || []).map((r: any) => r.id);
          const ownerIds = (ownersRes.data || []).map((r: any) => r.id);

          const orParts = [
            `ii_id.ilike.%${safe}%`,
            `title.ilike.%${safe}%`,
          ];
          if (cityIds.length > 0)   orParts.push(`city_id.in.(${cityIds.join(',')})`);
          if (sectorIds.length > 0) orParts.push(`sector_id.in.(${sectorIds.join(',')})`);
          if (ownerIds.length > 0) {
            // Owner name is PII gated like the detail screen (canViewOwner):
            // users who can see all owners match any agency property; restricted
            // agents only match properties they're assigned (whose owner they
            // can already see). owner_id.in stays correct even if a matched
            // contact isn't an owner of any property (it just won't match).
            orParts.push(
              canViewAllOwners(profile)
                ? `owner_id.in.(${ownerIds.join(',')})`
                : `and(owner_id.in.(${ownerIds.join(',')}),agent_id.eq.${profile.id})`,
            );
          }

          propsQuery = propsQuery.or(orParts.join(','));
        }
      }

      // Planner unlock + impersonation correctness. The agent + super_admin
      // RLS policies are both PERMISSIVE and OR together, so the planner
      // can't push agency_id down to a seek and degrades to scanning
      // idx_properties_active_created_at_desc and post-filtering thousands
      // of other agencies' rows. Adding agency_id to the WHERE explicitly
      // engages idx_properties_agency_created_at_desc and drops list queries
      // from 1.3s → sub-ms. RLS still enforces; this is just the planner
      // hint. Skip only for non-impersonating super_admin (sees all).
      if (profile?.agency_id && !(profile?.role === 'super_admin' && !isImpersonating)) {
        propsQuery = propsQuery.eq('agency_id', profile.agency_id);
      }

      // Every agent in the agency sees every property in the agency.
      // RLS already restricts to auth_agency_id(); no per-agent filter here.

      const { data: propsData, error: propsError, count } = await propsQuery;

      // Drop the result if a newer fetchProperties has been kicked off while
      // we were awaiting. Without this, an older call that returns 0 rows
      // (e.g. stale tab filter, aborted-then-retried request) overwrites a
      // newer call's populated array and the user sees the empty state.
      if (!propertiesReqId.isLatest(myReqId)) return;

      if (propsError) {
        console.error('Error fetching properties:', propsError);
        return;
      }
      if (!propsData) {
        // Null without an error usually means the request was aborted or
        // hit a transient network blip. Don't silently leave the list empty
        // — at least surface it so the bug is visible in logs.
        console.warn('fetchProperties: null data with no error (request likely aborted)');
        return;
      }

      {
        const mappedProps = propsData.map((p: any) => {
          const agentProfile = p.agent;
          return {
            ...p,
            property_type_name: p.property_types?.name,
            department_name: p.departments?.name,
            city_name: p.cities?.name,
            sector_name: p.sectors?.name,
            status: PROPERTY_STATUS_LABEL[p.property_status] ?? 'Disponible',
            // cover_photo_url comes from the spread above (denormalized column).
            // Full gallery is loaded on detail view, not in list. Empty array
            // here so consumers that read .gallery (PropertyCard, etc.) don't
            // crash on undefined.
            gallery: [],
            agent_name: agentProfile?.full_name,
            agent_photo: agentProfile?.photo_url,
            agent: agentProfile ? {
              name: agentProfile.full_name,
              avatar: agentProfile.photo_url,
              phone: agentProfile.phone,
              role: 'Asesor Inmobiliario',
            } : undefined,
            // Owner is fetched on the detail screen, gated by canViewOwner.
            property_types: undefined,
            departments: undefined,
            cities: undefined,
            sectors: undefined,
            property_photos: undefined
          };
        });

        if (append) {
          setProperties(prev => {
            const existingIds = new Set(prev.map(p => p.id));
            const newUnique = mappedProps.filter((p: any) => !existingIds.has(p.id));
            return [...prev, ...applyPendingOverlay('properties', newUnique)] as Property[];
          });
        } else {
          // Overlay archives still inside their undo window (lib/undoable.ts).
          setProperties(applyPendingOverlay('properties', mappedProps) as Property[]);
        }

        // Keep page state aligned with the most recent fetch so a follow-up
        // "Cargar más" pages from the right offset (a fresh filter call comes
        // in as page 1; without this the next click would jump to page N+1
        // where N was whatever happened to be in state).
        setPropertiesPage(page);
        setHasMoreProperties(propsData.length === ITEMS_PER_PAGE);
        // count is the exact total for the filter set, independent of the page
        // range — store it so the list can show a real "N propiedades" total.
        setPropertiesTotalCount(typeof count === 'number' ? count : null);
        setIsPropertiesInitialized(true);
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
    } finally {
      // Only the latest fetch resets the loading flag. A superseded fetch
      // that finishes after a newer one was kicked off would otherwise flip
      // the spinner off prematurely.
      if (propertiesReqId.isLatest(myReqId)) {
        setIsLoadingMoreProperties(false);
      }
    }
  }, [profile?.id, profile?.role, profile?.agency_id, isImpersonating]);

  const handleLoadMoreProperties = useCallback(() => {
    if (!hasMoreProperties || isLoadingMoreProperties) return;
    const nextPage = propertiesPage + 1;
    setPropertiesPage(nextPage);
    fetchProperties(nextPage, true);
  }, [hasMoreProperties, isLoadingMoreProperties, propertiesPage, fetchProperties]);

  // Paginated requirements fetch — mirrors fetchProperties. The
  // v_requirements_with_match_count view is heavy (joins property_types,
  // computes match_count); without paging, agencies with 10k+ requirements
  // freeze the browser on initial render (Gap 39).
  const fetchRequirements = useCallback(async (page: number, append: boolean = false, serverFilters?: RequirementListServerFilters) => {
    if (!supabase || !profile) return;
    // Persist filters so a follow-up "Cargar más" appends with the same
    // constraints; omit to reuse the last set (initial load = empty default).
    if (serverFilters !== undefined) {
      requirementFiltersRef.current = serverFilters;
    }
    const rFilters = requirementFiltersRef.current;
    const myReqId = requirementsReqId.next();
    try {
      setIsLoadingMoreRequirements(true);
      const from = (page - 1) * REQUIREMENTS_PAGE_SIZE;
      const to = from + REQUIREMENTS_PAGE_SIZE - 1;

      // 2026-05-05 hot-fix: switched from v_requirements_with_match_count
      // (slow correlated subquery, 2-6s per call) to the bare `requirements`
      // table. With agencies hitting this view on every CRM mount the view
      // saturated the PostgREST pool and caused site-wide PGRST003 timeouts.
      // The match_count column is lost here — RequirementsView still renders
      // without it (badge just hidden), and the dedicated /coincidencias
      // pages still compute it on demand. Restore once the view is replaced
      // with a materialized view or batched RPC.
      let reqsQuery = supabase
        .from('requirements')
        .select('*')
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .range(from, to);

      // Planner unlock — see fetchProperties for full rationale. RLS quals
      // can't push down through the OR'd permissive policies, so add the
      // agency_id explicitly to engage idx_requirements_agency_created_at_desc.
      if (profile?.agency_id && !(profile?.role === 'super_admin' && !isImpersonating)) {
        reqsQuery = reqsQuery.eq('agency_id', profile.agency_id);
      }

      const perms = (profile as any)?.permissions;
      const isAgent = profile?.role === 'agent';
      if (isAgent && profile?.id && !perms?.can_view_all_leads) {
        reqsQuery = reqsQuery.or(`agent_id.eq.${profile.id},created_by.eq.${profile.id}`);
      }

      // List filters pushed server-side so they cover the whole dataset, not
      // just the loaded page (RequirementsView toolbar — operation/status/agent/
      // type/date). Empty values are omitted by the caller.
      if (rFilters.operation) reqsQuery = reqsQuery.eq('operation', rFilters.operation);
      if (rFilters.status) reqsQuery = reqsQuery.eq('status', rFilters.status);
      if (rFilters.agent_id) reqsQuery = reqsQuery.eq('agent_id', rFilters.agent_id);
      if (rFilters.property_type_ids && rFilters.property_type_ids.length > 0) {
        reqsQuery = reqsQuery.overlaps('property_type_ids', rFilters.property_type_ids);
      }
      if (rFilters.created_from) reqsQuery = reqsQuery.gte('created_at', rFilters.created_from);
      if (rFilters.created_to) reqsQuery = reqsQuery.lte('created_at', `${rFilters.created_to}T23:59:59.999Z`);

      const { data: reqsData, error: reqsError } = await reqsQuery;
      // See fetchProperties for the rationale — a stale parallel fetch must
      // not stomp the latest result with empty/old rows.
      if (!requirementsReqId.isLatest(myReqId)) return;
      if (reqsData && !reqsError) {
        if (append) {
          setRequirements(prev => {
            const existingIds = new Set(prev.map(r => r.id));
            const newUnique = (reqsData as any[]).filter(r => !existingIds.has(r.id));
            return [...prev, ...applyPendingOverlay('requirements', newUnique)] as Requirement[];
          });
        } else {
          setRequirements(applyPendingOverlay('requirements', reqsData as any[]) as Requirement[]);
        }
        setHasMoreRequirements(reqsData.length === REQUIREMENTS_PAGE_SIZE);
        setIsRequirementsInitialized(true);
      } else if (reqsError) {
        console.error('Error fetching requirements:', reqsError);
      } else {
        console.warn('fetchRequirements: null data with no error (request likely aborted)');
      }
    } catch (err) {
      console.error('Error fetching requirements:', err);
    } finally {
      if (requirementsReqId.isLatest(myReqId)) {
        setIsLoadingMoreRequirements(false);
      }
    }
  }, [profile?.id, profile?.role, profile?.agency_id, isImpersonating]);

  const handleLoadMoreRequirements = useCallback(() => {
    if (!hasMoreRequirements || isLoadingMoreRequirements) return;
    const nextPage = requirementsPage + 1;
    setRequirementsPage(nextPage);
    fetchRequirements(nextPage, true);
  }, [hasMoreRequirements, isLoadingMoreRequirements, requirementsPage, fetchRequirements]);

  // Planner unlock — see fetchProperties. The contacts RLS quals are OR'd
  // PERMISSIVE, so the planner can't push agency_id down on its own; passing
  // it explicitly engages idx_contacts_agency_list_sort_desc instead of a
  // seq scan + sort over every agency's contacts. super_admin (not
  // impersonating) is exempt so it still sees all agencies.
  const contactsAgencyScope = profile?.agency_id && !(profile?.role === 'super_admin' && !isImpersonating)
    ? profile.agency_id
    : null;

  // Free-text search — case-insensitive partial across name/phone/email
  // + tags, as ONE OR string reused verbatim by every contacts query so their
  // row sets stay identical. Same sanitization pattern as the properties
  // query: strip PostgREST OR-filter special chars (",()*) before
  // interpolation. legacy_id is integer, so only matched when the input is
  // digits-only. Tags live in the contact_tags junction and PostgREST can't OR
  // base columns with an embedded filter, so matching tag rows are resolved to
  // contact ids first and OR'd in (capped at 100 ids to keep the request URL
  // within budget).
  const resolveContactSearchOr = useCallback(async (search: string | undefined): Promise<string | null> => {
    const safe = search?.trim().replace(/[(),*]/g, '');
    if (!supabase || !safe) return null;
    const orParts = [
      `name.ilike.%${safe}%`,
      `phone.ilike.%${safe}%`,
      `alternate_phone.ilike.%${safe}%`,
      `email.ilike.%${safe}%`,
    ];
    if (/^\d+$/.test(safe)) orParts.push(`legacy_id.eq.${safe}`);
    let tagHitsQuery = supabase
      .from('contact_tags')
      .select('contact_id')
      .ilike('tag', `%${safe}%`)
      .limit(100);
    if (isImpersonating && profile?.agency_id) {
      tagHitsQuery = tagHitsQuery.eq('agency_id', profile.agency_id);
    }
    const { data: tagHits } = await tagHitsQuery;
    const tagIds = Array.from(new Set((tagHits ?? []).map((t: any) => t.contact_id).filter(Boolean)));
    if (tagIds.length > 0) orParts.push(`id.in.(${tagIds.join(',')})`);
    return orParts.join(',');
  }, [isImpersonating, profile?.agency_id]);

  // head:true → COUNT only (no rows), one indexed count — not the per-row
  // subqueries that were pulled from the list SELECT for performance.
  const countContactsWith = useCallback(async (
    filters: ContactListServerFilters,
    searchOr: string | null,
    intakeColumnId: string | undefined,
    role: 'lead' | 'owner',
    hiddenColumnIds?: string[],
  ): Promise<number | null> => {
    if (!supabase) return null;
    let q: any = supabase
      .from('contacts')
      .select(`id${tagEmbed(filters)}`, { count: 'exact', head: true })
      .eq(role === 'lead' ? 'is_lead' : 'is_owner', true);
    if (contactsAgencyScope) q = q.eq('agency_id', contactsAgencyScope);
    q = narrowContacts(q, filters, searchOr, intakeColumnId);
    if (hiddenColumnIds) {
      q = q.eq('is_deleted', false);
      if (hiddenColumnIds.length > 0) {
        q = q.or(`pipeline_column_id.is.null,pipeline_column_id.not.in.(${hiddenColumnIds.join(',')})`);
      }
    }
    const { count } = await q;
    return typeof count === 'number' ? count : null;
  }, [contactsAgencyScope]);

  const countContacts = useCallback(async (filters: ContactListServerFilters, role: 'lead' | 'owner', hiddenColumnIds?: string[]) =>
    countContactsWith(filters, await resolveContactSearchOr(filters.search), intakeColumnIdOf(leadsColumnsRef.current), role, hiddenColumnIds),
  [countContactsWith, resolveContactSearchOr]);

  // Board header totals (is_lead / is_owner) under the active list filters, so
  // "N Prospectos" is the set the board pages through — an asesor's clients,
  // not the whole agency. RLS scopes to the caller's visible rows.
  const contactTotalsReqId = useRequestId();
  const updateContactTotals = useCallback(async (filters: ContactListServerFilters, searchOr: string | null, intakeColumnId: string | undefined) => {
    const myId = contactTotalsReqId.next();
    const [leadsCount, ownersCount] = await Promise.all([
      countContactsWith(filters, searchOr, intakeColumnId, 'lead'),
      countContactsWith(filters, searchOr, intakeColumnId, 'owner'),
    ]);
    if (!contactTotalsReqId.isLatest(myId)) return;
    setLeadsTotalCount(leadsCount);
    setOwnersTotalCount(ownersCount);
  }, [countContactsWith, contactTotalsReqId]);

  // Paginated contacts fetch — same Gap 39 fix as requirements. The unbounded
  // query with seguimientos(count) + contact_properties(count) joins is what
  // froze the kanban on big agencies (Olga 12188, CityMax 10655). Keep the
  // post-fetch leads/owners split + permission filtering identical to the
  // sample-era code; only the .range() and append behavior are new.
  const fetchContacts = useCallback(async (page: number, append: boolean = false, leadColsAtFetch?: PipelineColumn[], serverFilters?: ContactListServerFilters) => {
    if (!supabase || !profile) return;
    // Persist filters across pagination — fetchContacts(nextPage, true) from
    // handleLoadMoreContacts comes with no 4th arg and must reuse the active
    // filters so the server narrow stays applied as the user pages through.
    if (serverFilters !== undefined) {
      contactFiltersRef.current = serverFilters;
    }
    const filters = contactFiltersRef.current;
    const myReqId = contactsReqId.next();
    // A fresh page-1 load (new filters, reset, init) restarts "Cargar más" at
    // page 2 — it used to keep counting from wherever the last filter left off.
    setContactsPage(page);
    const colsForPriority = leadColsAtFetch ?? leadsColumnsRef.current;
    const intakeColumnId = intakeColumnIdOf(colsForPriority);
    try {
      setIsLoadingMoreContacts(true);
      const from = (page - 1) * CONTACTS_PAGE_SIZE;
      const to = from + CONTACTS_PAGE_SIZE - 1;

      const searchOrFilter = await resolveContactSearchOr(filters.search);
      // A fresh load also recounts the header totals under the same filters.
      if (!append) void updateContactTotals(filters, searchOrFilter, intakeColumnId);

      // Tag filter rides on an ALIASED inner-join embed (tagEmbed) so the
      // parent row set narrows to contacts having the tag, while any plain
      // contact_tags embed (enrichment) keeps returning the full tag list.
      // Super admin (unscoped) sees every agency's contacts: the board's agency
      // agents list can't name them, so join agent + agency names here.
      const ownerEmbed = contactsAgencyScope ? '' : ', agent:profiles!contacts_agent_id_fkey(full_name), agency:agencies(name)';
      let contactsQuery: any = supabase
        .from('contacts')
        .select(`*${ownerEmbed}${tagEmbed(filters)}`)
        // list_sort_at = GREATEST(created_at, last_inbound_at): a returning
        // contact (agency-site form) sorts as new. Computed field, see
        // 20260921180000_contact_inbound_bump.
        .order('list_sort_at', { ascending: false })
        .range(from, to);
      if (contactsAgencyScope) contactsQuery = contactsQuery.eq('agency_id', contactsAgencyScope);
      contactsQuery = narrowContacts(contactsQuery, filters, searchOrFilter, intakeColumnId);

      // Per-agent visibility is enforced at the RLS layer
      // (20260512130000_contacts_rls_can_view_all_gates) — any row the caller
      // is not allowed to see is filtered server-side. Client-side gating used
      // to live here as belt-and-suspenders but drifted from RLS (the list
      // missed owners of properties the caller was the agent on, even though
      // PropertyDetailView showed them). Single source of truth wins.

      const { data: contactsData, error: contactsError } = await contactsQuery;

      // See fetchProperties — a superseded fetch must not stomp the latest.
      if (!contactsReqId.isLatest(myReqId)) return;

      if (contactsData && !contactsError) {
        // Card badges (seguimientos_count, properties_shown_count) are filled
        // in by the background enrichment query below — leaving them off the
        // initial mapping keeps the SELECT free of the two count subqueries
        // that froze the kanban on big agencies (Olga 12k, CityMax 10k).
        // filter_tags is the inner-join helper embed, not contact data.
        const mapped = applyPendingOverlay('contacts', contactsData.map(({ filter_tags: _ft, agent, agency, ...c }: any) => ({
          ...c,
          ...(agent?.full_name ? { agent_name: agent.full_name } : {}),
          ...(agency?.name ? { agency_name: agency.name } : {}),
        })));

        // Partition into the two UI tabs. RLS already filtered by per-agent
        // visibility, so no extra gate is needed here — just split on the role
        // flags. A contact who is both lead AND owner appears in both tabs.
        const filteredLeads = mapped.filter((c: any) => c.is_lead) as Contact[];
        const filteredOwners = mapped.filter((c: any) => c.is_owner) as Contact[];

        if (append) {
          setLeads(prev => {
            const existingIds = new Set(prev.map(l => l.id));
            const newUnique = filteredLeads.filter((l: any) => !existingIds.has(l.id));
            return applyLeadPriorities([...prev, ...newUnique] as Contact[], colsForPriority);
          });
          setOwners(prev => {
            const existingIds = new Set(prev.map(o => o.id));
            const newUnique = filteredOwners.filter((o: any) => !existingIds.has(o.id));
            return [...prev, ...newUnique] as Contact[];
          });
        } else {
          setLeads(applyLeadPriorities(filteredLeads, colsForPriority));
          setOwners(filteredOwners);
        }

        setHasMoreContacts(contactsData.length === CONTACTS_PAGE_SIZE);

        // Background enrichment: re-run the same filtered query for just
        // (id, seguimientos count, contact_properties count) so the badges
        // populate after first paint. We re-apply the same range + agency +
        // agent-permission filters instead of filtering by .in('id', ids) to
        // keep the URL short — 500 UUIDs would blow PostgREST's URL budget.
        if (contactsData.length > 0) {
          // Same narrow (incl. the tag-resolved search ids) so the row IDs
          // match the primary fetch. The plain contact_tags embed stays
          // unfiltered for display.
          let countsQuery: any = supabase
            .from('contacts')
            .select(`id, seguimientos(count), contact_properties(property_id, created_at, properties(id, title, ii_id)), contact_tags(tag)${tagEmbed(filters)}`)
            .order('list_sort_at', { ascending: false })
            .range(from, to);
          if (contactsAgencyScope) countsQuery = countsQuery.eq('agency_id', contactsAgencyScope);
          countsQuery = narrowContacts(countsQuery, filters, searchOrFilter, intakeColumnId);
          void countsQuery.then(({ data: countsData, error: countsError }: { data: any[] | null; error: unknown }) => {
            if (!countsData || countsError) return;
            // Guard the background enrichment too — a stale enrichment from
            // a superseded fetch would otherwise patch the wrong card set.
            if (!contactsReqId.isLatest(myReqId)) return;
            // Property of interest = the newest link the agency may see. A link
            // with a null properties embed is either another agency's listing
            // (properties RLS is agency-scoped) or an own listing permanently
            // deleted; those are classified in ONE batch call per page below
            // (linked_property_summaries: title / unavailable / hidden).
            const pickPoi = (links: any[], summaries?: Map<string, any>): Contact['property_of_interest'] => {
              for (const cp of links) {
                if (cp.properties) return { id: cp.properties.id, title: cp.properties.title, ii_id: cp.properties.ii_id };
                const sm = summaries?.get(String(cp.property_id));
                if (sm) return sm.unavailable ? { id: sm.id, unavailable: true } : { id: sm.id, title: sm.title, ii_id: sm.ii_id };
              }
              return null;
            };
            const countsById = new Map<string, { s: number; cp: number; tags: string[]; poi: Contact['property_of_interest'] }>();
            const linksById = new Map<string, any[]>();
            const hiddenIds = new Set<string>();
            for (const c of countsData as any[]) {
              const links = ((c.contact_properties ?? []) as any[])
                .slice()
                .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
              linksById.set(c.id, links);
              // ponytail: only the 20 newest hidden links before the first visible one
              // are classified — legacy leads carry hundreds of links to deleted listings.
              for (const cp of links.slice(0, 20)) {
                if (cp.properties) break;
                hiddenIds.add(String(cp.property_id));
              }
              countsById.set(c.id, {
                s: c.seguimientos?.[0]?.count || 0,
                cp: links.length,
                tags: (c.contact_tags ?? []).map((t: any) => t.tag).filter(Boolean),
                poi: pickPoi(links),
              });
            }
            if (hiddenIds.size > 0) {
              void supabase!.rpc('linked_property_summaries', { p_property_ids: [...hiddenIds] }).then(({ data, error }) => {
                if (error || !data?.length || !contactsReqId.isLatest(myReqId)) return;
                const summaries = new Map((data as any[]).map(d => [String(d.id), d]));
                const patch = (list: Contact[]) => list.map((c: any) => {
                  const links = linksById.get(c.id);
                  return links ? { ...c, property_of_interest: pickPoi(links, summaries) } : c;
                });
                setLeads(prev => patch(prev));
                setOwners(prev => patch(prev));
              });
            }
            const applyCounts = (list: Contact[]) => list.map((c: any) => {
              const n = countsById.get(c.id);
              return n ? { ...c, seguimientos_count: n.s, properties_shown_count: n.cp, tags: n.tags, property_of_interest: n.poi } : c;
            });
            setLeads(prev => applyCounts(prev));
            setOwners(prev => applyCounts(prev));
          });
        }
      } else if (contactsError) {
        console.error('Error fetching contacts:', contactsError);
      } else {
        console.warn('fetchContacts: null data with no error (request likely aborted)');
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      if (contactsReqId.isLatest(myReqId)) {
        setIsLoadingMoreContacts(false);
      }
    }
  }, [profile?.id, profile?.role, profile?.agency_id, isImpersonating, contactsAgencyScope, resolveContactSearchOr, updateContactTotals]);

  const handleLoadMoreContacts = useCallback(() => {
    if (!hasMoreContacts || isLoadingMoreContacts) return;
    const nextPage = contactsPage + 1;
    setContactsPage(nextPage);
    fetchContacts(nextPage, true);
  }, [hasMoreContacts, isLoadingMoreContacts, contactsPage, fetchContacts]);

  // Recount the header totals after an add/delete, under the active filters.
  const refreshContactCounts = useCallback(async () => {
    if (!profile) return;
    const filters = contactFiltersRef.current;
    const searchOr = await resolveContactSearchOr(filters.search);
    await updateContactTotals(filters, searchOr, intakeColumnIdOf(leadsColumnsRef.current));
  }, [profile?.id, resolveContactSearchOr, updateContactTotals]);

  // ── Registrar venta modal (shared, app-wide) ──────────────
  // Mounted once in the provider so both entry points (LeadModal "Vinculadas"
  // and the property StatusPicker "Vendido") can open it without prop-drilling.
  const [recordSaleTarget, setRecordSaleTarget] = useState<{ property: Property; contact: Contact | null } | null>(null);
  const openRecordSale = useCallback((property: Property, contact?: Contact | null) => {
    setRecordSaleTarget({ property, contact: contact ?? null });
  }, []);

  const handleStatusChange = useCallback(async (id: string, newStatus: string) => {
    if (blockReadOnly()) return;
    // StatusPicker emits Spanish labels; the DB CHECK only allows the English
    // enum (available/reserved/sold/rented/off_market). Translate before write.
    const dbStatus = PROPERTY_STATUS_ENUM[newStatus] ?? newStatus;

    // Selling is more than a status flip — it records the buyer, price and
    // commission, and pulls the listing off the portals. Route 'sold' through
    // the Registrar Venta modal (record_property_sale RPC) instead of a bare
    // property_status write. If the agent cancels the modal, nothing changes.
    if (dbStatus === 'sold') {
      const prop = properties.find(p => p.id === id);
      if (prop) { openRecordSale(prop); return; }
    }

    // Terminal statuses (sold/rented/off_market) usually mean the listing
    // should also come off the public portals and out of the Intercambio
    // share network. Without this prompt, a sold property can stay live on
    // mapainmueble.com and continue matching in Intercambio until the agent
    // remembers to flip both toggles by hand.
    const terminal = dbStatus === 'sold' || dbStatus === 'rented' || dbStatus === 'off_market';
    let extraUpdate: Record<string, unknown> = {};
    if (terminal) {
      const prop = properties.find(p => p.id === id);
      const isPublished = prop?.publish_status === 'published';
      const isShared = prop?.share_status === 'shared';
      if (isPublished || isShared) {
        const surfaces = [
          isPublished ? 'los portales' : null,
          isShared ? 'Intercambio' : null,
        ].filter(Boolean).join(' y ');
        if (await confirmDialog(`¿Quieres también quitar esta propiedad de ${surfaces}?`)) {
          const now = new Date().toISOString();
          if (isPublished) {
            extraUpdate.publish_status = 'unpublished';
            extraUpdate.unpublished_at = now;
          }
          if (isShared) {
            extraUpdate.share_status = 'not_shared';
            extraUpdate.unshared_at = now;
          }
        }
      }
    }

    setProperties(prev => prev.map(p =>
      p.id === id
        ? {
            ...p,
            status: newStatus,
            property_status: dbStatus,
            ...(extraUpdate.publish_status ? { publish_status: 'unpublished' as const } : {}),
            ...(extraUpdate.share_status ? { share_status: 'not_shared' as const } : {}),
          }
        : p
    ));
    const { error } = await (supabase?.from('properties')
      .update({ property_status: dbStatus, ...extraUpdate })
      .eq('id', id) ?? Promise.resolve({ error: null } as any));
    if (error) console.error('handleStatusChange failed:', error);
  }, [blockReadOnly, properties, openRecordSale]);

  const handlePublishToggle = useCallback(async (id: string) => {
    if (blockReadOnly()) return;
    const prop = properties.find(p => p.id === id);
    if (!prop) return;

    if (prop.publish_status === 'published' && !isInPublishRenewalWindow(prop.publish_expires_at)) {
      // Outside the renewal window — clicking the toggle unpublishes.
      setProperties(prev => prev.map(p => p.id === id ? { ...p, publish_status: 'unpublished' } : p));
      supabase?.from('properties').update({ publish_status: 'unpublished', unpublished_at: new Date().toISOString() }).eq('id', id).then();
    } else {
      // One of three cases:
      //   - status='unpublished' → initial publish
      //   - status='expired' → renew after expiry
      //   - status='published' AND within 7 days of expiry → early renewal
      // All three flow through renew_property_publishing. Only the two renewals
      // reset published_at (back to the top of the portal "recent" sort);
      // re-publishing an unpublished listing keeps its date — the RPC returns
      // the real one.
      if (supabase) {
        const { data: limitCheck } = await supabase.rpc('check_publish_limit');
        if (limitCheck && !limitCheck.allowed) {
          alert(limitCheck.reason || 'Límite de publicaciones alcanzado');
          return;
        }
      }
      setProperties(prev => prev.map(p => p.id === id ? { ...p, publish_status: 'published' } : p));
      supabase?.rpc('renew_property_publishing', { p_property_id: id }).then(({ data }) => {
        if (data?.error) {
          alert(data.message || data.error);
          return;
        }
        if (data?.expires_at) {
          setProperties(prev2 => prev2.map(pp => pp.id === id ? { ...pp, publish_expires_at: data.expires_at, published_at: data.published_at ?? pp.published_at } : pp));
        }
      });
    }
  }, [properties, blockReadOnly]);

  const handleShareToggle = useCallback(async (id: string) => {
    if (blockReadOnly()) return;
    const prop = properties.find(p => p.id === id);
    if (!prop) return;

    if (prop.share_status === 'shared' && !isInShareRenewalWindow(prop.share_expires_at)) {
      // Outside the renewal window — clicking the toggle unshares.
      setProperties(prev => prev.map(p => p.id === id ? { ...p, share_status: 'not_shared' } : p));
      supabase?.from('properties').update({ share_status: 'not_shared', unshared_at: new Date().toISOString() }).eq('id', id).then();
    } else {
      // One of three cases (mirrors handlePublishToggle above):
      //   - status='not_shared' → initial share
      //   - status='expired'    → renew after expiry
      //   - status='shared' AND within 30 days of expiry → early renewal
      // The third case used to fall into the unshare branch, so the button the
      // dashboard sent agents to press removed the listing from the network
      // instead of renewing it.
      // Check share limit before sharing
      if (supabase) {
        const { data: limitCheck } = await supabase.rpc('check_share_limit');
        if (limitCheck && !limitCheck.allowed) {
          alert(limitCheck.reason || 'Límite de compartidas alcanzado');
          return;
        }
        // Intercambio rules: no phone in description, no watermark on cover.
        const compliance = await checkShareCompliance(supabase, id);
        if (!compliance.allowed) {
          const wantsAppeal = await confirmDialog(
            `${compliance.reasons.join('\n')}\n\n` +
            '¿Crees que es un error? Puedes apelar para que un administrador lo revise.',
            { title: 'No se puede compartir en Intercambio', confirmLabel: 'Apelar', cancelLabel: 'Cerrar', danger: false }
          );
          if (wantsAppeal) {
            const message = prompt('Cuéntanos por qué crees que la detección es incorrecta (opcional):') ?? undefined;
            const result = await submitShareAppeal(supabase, id, message);
            if (result.ok) {
              // The RPC re-shared the property while the appeal is reviewed.
              setProperties(prev => prev.map(p => p.id === id ? { ...p, share_status: 'shared', shared_at: new Date().toISOString() } : p));
            }
            alert(result.message);
          }
          return;
        }
      }
      // Share (or renew expired)
      setProperties(prev => prev.map(p => p.id === id ? { ...p, share_status: 'shared', shared_at: new Date().toISOString() } : p));
      supabase?.rpc('renew_property_sharing', { p_property_id: id }).then(({ data }) => {
        if (data?.expires_at) {
          setProperties(prev2 => prev2.map(pp => pp.id === id ? { ...pp, share_expires_at: data.expires_at } : pp));
        }
      });
      // notify_property_shared is a no-op stub (matching paused, see migration
      // 20260506030000_stub_notify_match_wrappers). Skipping the call avoids
      // a wasted network round-trip per share. Restore in tandem with the
      // matching rebuild.
    }
  }, [properties, blockReadOnly]);

  // First-fetch sentinels. Consumers gate their empty state on these so
  // "Sin resultados" doesn't flash while the lazy fetch is in flight.
  const [isSharedPropertiesLoaded, setIsSharedPropertiesLoaded] = useState(false);
  const [isSharedRequirementsLoaded, setIsSharedRequirementsLoaded] = useState(false);
  // In-flight guards. Two consumers mounting in the same render (e.g.
  // KanbanPage prefetch + ExchangePropertiesPage tab switch) shouldn't
  // fire two parallel get_shared_properties calls.
  const sharedPropertiesInFlightRef = useRef(false);
  const sharedRequirementsInFlightRef = useRef(false);

  // Fetch shared properties from other agencies (Intercambio)
  const fetchSharedProperties = useCallback(async () => {
    if (!supabase || !profile) return;
    if (sharedPropertiesInFlightRef.current) return;
    sharedPropertiesInFlightRef.current = true;
    const myReqId = sharedPropertiesReqId.next();
    try {
      const { data, error } = await supabase.rpc('get_shared_properties');
      if (!sharedPropertiesReqId.isLatest(myReqId)) return;
      if (data && !error) {
        setSharedProperties(data.map((p: any) => ({
          ...p,
          status: PROPERTY_STATUS_LABEL[p.property_status] ?? 'Disponible',
          sharedBy: {
            name: p.agent_name || p.agency_name || 'Agente',
            avatar: p.agent_photo,
            agencyName: p.agency_name,
            agencyLogo: p.agency_logo,
            agencyLogoBg: p.agency_logo_bg_color,
          },
          // Agent contact card on the Intercambio detail view reads from
          // property.agent.{name,avatar,phone}; populate it from the RPC
          // columns so cross-agency contact buttons (message/whatsapp/call)
          // have the data they need.
          agent: p.agent_id ? {
            id: p.agent_id,
            name: p.agent_name,
            avatar: p.agent_photo,
            phone: p.agent_phone,
            role: 'Asesor Inmobiliario',
          } : undefined,
        })) as Property[]);
        setIsSharedPropertiesLoaded(true);
      } else if (error) {
        console.error('Error fetching shared properties:', error);
      } else {
        console.warn('fetchSharedProperties: null data with no error (request likely aborted)');
      }
    } catch (err) {
      console.error('Error fetching shared properties:', err);
    } finally {
      sharedPropertiesInFlightRef.current = false;
    }
  }, [profile?.id, sharedPropertiesReqId]);

  // Idempotent first-fetch — call from any consumer that needs the
  // shared list. Subsequent calls (after first fetch resolved or while
  // one is in flight) are cheap no-ops.
  const ensureSharedProperties = useCallback(async () => {
    if (isSharedPropertiesLoaded || sharedPropertiesInFlightRef.current) return;
    await fetchSharedProperties();
  }, [isSharedPropertiesLoaded, fetchSharedProperties]);

  // Fetch shared requirements from other agencies (Intercambio)
  const fetchSharedRequirements = useCallback(async () => {
    if (!supabase || !profile) return;
    if (sharedRequirementsInFlightRef.current) return;
    sharedRequirementsInFlightRef.current = true;
    const myReqId = sharedRequirementsReqId.next();
    try {
      const { data, error } = await supabase.rpc('get_shared_requirements');
      if (!sharedRequirementsReqId.isLatest(myReqId)) return;
      if (data && !error) {
        setSharedRequirements(data.map((r: any) => ({
          ...r,
          sharedBy: {
            name: r.agent_name || r.agency_name || 'Agente',
            avatar: r.agent_photo,
            agencyName: r.agency_name,
            agencyLogo: r.agency_logo,
          },
        })) as Requirement[]);
        setIsSharedRequirementsLoaded(true);
      } else if (error) {
        console.error('Error fetching shared requirements:', error);
      } else {
        console.warn('fetchSharedRequirements: null data with no error (request likely aborted)');
      }
    } catch (err) {
      console.error('Error fetching shared requirements:', err);
    } finally {
      sharedRequirementsInFlightRef.current = false;
    }
  }, [profile?.id, sharedRequirementsReqId]);

  const ensureSharedRequirements = useCallback(async () => {
    if (isSharedRequirementsLoaded || sharedRequirementsInFlightRef.current) return;
    await fetchSharedRequirements();
  }, [isSharedRequirementsLoaded, fetchSharedRequirements]);

  // Initial data fetch
  useEffect(() => {
    if (!profile) return;

    async function fetchData() {
      if (!supabase) return;
      try {
        // Fire everything that does NOT depend on pipeline_columns immediately,
        // so the properties list (the usual landing) starts loading without
        // first waiting a round-trip for the columns.
        //
        // fetchProperties reads the filters the user persisted in sessionStorage
        // so its prefilled fetch matches what PropiedadesView's filter effect
        // will request (deduped by the request-id guard) — the user lands on the
        // page with data instead of waiting twice.
        fetchProperties(1, false, readPropertiesFiltersFromSession());
        fetchRequirements(1, false);
        // Header totals come with the fetchContacts(1, …) below.

        // Projects — independent of everything else; run in parallel and await
        // at the end so it never blocks contacts.
        const proyectosPromise = (async () => {
          let projQuery = supabase!
            .from('proyectos')
            // properties(count) = assigned listings via properties.proyecto_id.
            // The is_deleted filter on the embed is NOT redundant with RLS: the
            // agency SELECT policy is OR'd with one that only checks
            // permanently_deleted_at, so soft-deleted rows are readable and
            // would otherwise be counted.
            .select('*, properties(count)')
            .eq('is_deleted', false)
            .eq('properties.is_deleted', false)
            .order('nombre');
          if (isImpersonating && profile?.agency_id) {
            projQuery = projQuery.eq('agency_id', profile.agency_id);
          }
          const { data: projData, error: projError } = await projQuery;
          if (projData && !projError) {
            setProyectos(projData.map(({ properties, ...p }: any) => ({
              ...p,
              // Map snake_case DB columns to camelCase for UI compatibility
              unidadesTotales: p.unidades_totales,
              unidadesDisponibles: p.unidades_disponibles,
              asignadas: properties?.[0]?.count ?? 0,
            })) as Proyecto[]);
          }
        })();

        // Pipeline columns (lead vs owner) → then contacts, which needs the lead
        // columns for applyLeadPriorities. This is the ONLY serialized pair.
        let fetchedLeadCols: PipelineColumn[] = [];
        if (profile?.agency_id) {
          const { data: columnsData, error: columnsError } = await supabase
            .from('pipeline_columns')
            .select('*')
            .eq('agency_id', profile.agency_id)
            .order('sort_order', { ascending: true });

          fetchedLeadCols = columnsData && !columnsError && columnsData.length > 0
            ? columnsData.filter((c: any) => c.pipeline_type === 'lead') as PipelineColumn[]
            : [];
          const fetchedOwnerCols = columnsData && !columnsError && columnsData.length > 0
            ? columnsData.filter((c: any) => c.pipeline_type === 'owner') as PipelineColumn[]
            : [];
          if (fetchedLeadCols.length > 0) setLeadsColumns(fetchedLeadCols);
          if (fetchedOwnerCols.length > 0) setOwnersColumns(fetchedOwnerCols);
        }
        fetchContacts(1, false, fetchedLeadCols);

        await proyectosPromise;

        // Intercambio (cross-agency shared lists) used to load eagerly here.
        // That meant every CRM page load paid for ~500 shared properties +
        // ~300 shared requirements even if the user never opened the
        // Intercambio tab. Now they lazy-load via ensureSharedProperties /
        // ensureSharedRequirements, called from the consuming pages
        // (ExchangePropertiesPage, ExchangeRequirementsPage, KanbanPage).
        // Reset the loaded flags here so a profile/agency switch
        // (impersonation, sign-in/out) discards the previous tenant's
        // cached lists and the next consumer fires a fresh fetch.
        setIsSharedPropertiesLoaded(false);
        setIsSharedRequirementsLoaded(false);

        setIsDataLoaded(true);
      } catch (err) {
        console.error('Error fetching data from Supabase:', err);
      }
    }

    fetchData();
  }, [profile?.id, profile?.role, profile?.agency_id, isImpersonating, fetchProperties, fetchRequirements, fetchContacts]);

  // Re-fetch pipeline columns for the current agency. Called by the settings
  // editor after an agency admin renames/recolors stages so the Kanban boards
  // pick up the change without a full reload.
  const refreshPipelineColumns = useCallback(async () => {
    if (!supabase || !profile?.agency_id) return;
    const { data, error } = await supabase
      .from('pipeline_columns')
      .select('*')
      .eq('agency_id', profile.agency_id)
      .order('sort_order', { ascending: true });
    if (data && !error) {
      setLeadsColumns(data.filter((c: any) => c.pipeline_type === 'lead') as PipelineColumn[]);
      setOwnersColumns(data.filter((c: any) => c.pipeline_type === 'owner') as PipelineColumn[]);
    }
  }, [profile?.agency_id]);

  // Memoize the context value so a provider re-render that DOESN'T change any
  // exposed field (e.g. the page counters, recordSaleTarget) keeps the same
  // identity and doesn't re-render all 18 useCRM() consumers. useState setters
  // are stable and omitted from deps; the rest are listed.
  const contextValue = useMemo(() => ({
    properties, setProperties,
    sharedProperties, setSharedProperties,
    fetchProperties,
    handleStatusChange, handlePublishToggle, handleShareToggle,
    openRecordSale,
    hasMoreProperties, propertiesTotalCount, isLoadingMoreProperties, isPropertiesInitialized, handleLoadMoreProperties,
    leads, setLeads,
    owners, setOwners,
    hasMoreContacts, isLoadingMoreContacts, handleLoadMoreContacts, fetchContacts,
    leadsTotalCount, ownersTotalCount, refreshContactCounts, countContacts,
    leadsColumns, setLeadsColumns,
    ownersColumns, setOwnersColumns,
    refreshPipelineColumns,
    requirements, setRequirements,
    sharedRequirements, setSharedRequirements,
    hasMoreRequirements, isLoadingMoreRequirements, isRequirementsInitialized, handleLoadMoreRequirements, fetchRequirements,
    isSharedPropertiesLoaded, isSharedRequirementsLoaded,
    ensureSharedProperties, ensureSharedRequirements,
    fetchSharedProperties, fetchSharedRequirements,
    proyectos, setProyectos,
    isDataLoaded,
  }), [
    properties, sharedProperties, fetchProperties,
    handleStatusChange, handlePublishToggle, handleShareToggle, openRecordSale,
    hasMoreProperties, propertiesTotalCount, isLoadingMoreProperties, isPropertiesInitialized, handleLoadMoreProperties,
    leads, owners, hasMoreContacts, isLoadingMoreContacts, handleLoadMoreContacts, fetchContacts,
    leadsTotalCount, ownersTotalCount, refreshContactCounts, countContacts, leadsColumns, ownersColumns,
    requirements, sharedRequirements, hasMoreRequirements, isLoadingMoreRequirements, isRequirementsInitialized, handleLoadMoreRequirements, fetchRequirements,
    isSharedPropertiesLoaded, isSharedRequirementsLoaded, ensureSharedProperties, ensureSharedRequirements, fetchSharedProperties, fetchSharedRequirements,
    proyectos, isDataLoaded,
    refreshPipelineColumns,
  ]);

  return (
    <CRMContext.Provider value={contextValue}>
      {children}
      {recordSaleTarget && (
        <RegistrarVentaModal
          property={recordSaleTarget.property}
          contact={recordSaleTarget.contact}
          onClose={() => setRecordSaleTarget(null)}
          onRecorded={() => fetchProperties(1, false, readPropertiesFiltersFromSession())}
        />
      )}
    </CRMContext.Provider>
  );
};

export const useCRM = () => {
  const context = useContext(CRMContext);
  if (!context) throw new Error('useCRM must be used within CRMProvider');
  return context;
};
